import { Link } from "react-router-dom";
import { Buildings, Bank, Stack, Sparkle, Handshake, ChartLineUp, Rocket, ArrowRight, Lightning } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

/**
 * Orixa Builder — structuring & execution module.
 *
 * Each card launches a workflow that turns a validated opportunity into an
 * executable corporate structure (Company / Holding / SPV / Governance / etc.).
 */
const BUILDER_CARDS = [
  {
    id: "create_holding",
    icon: Bank,
    accent: "navy",
    title: "Holding Structuring",
    tagline: "Design a multi-jurisdictional holding — tax, governance, ownership.",
    output: "Jurisdiction matrix · Cap table · Governance charter · 180-day roadmap",
  },
  {
    id: "launch_business",
    icon: Rocket,
    accent: "teal",
    title: "Company Structuring",
    tagline: "Incorporate an operating company — jurisdiction, legal form, share design.",
    output: "Recommended jurisdiction · Legal structure · Founders' equity · ESOP design",
  },
  {
    id: "structure_deal",
    icon: Stack,
    accent: "gold",
    title: "SPV & Deal Structuring",
    tagline: "Capital stack design, DD checklist, term sheet, transaction roadmap.",
    output: "Capital stack · Term sheet · DD checklist · Closing timeline",
  },
  {
    id: "raise_capital",
    icon: ChartLineUp,
    accent: "teal",
    title: "Capital Readiness",
    tagline: "Investor memo, model, deck framework and a pipeline of named investors.",
    output: "IM · Financial model · Deck framework · Investor pipeline",
  },
];

const ACCENT_MAP = {
  teal: "bg-teal-soft text-teal",
  gold: "bg-gold-soft text-gold",
  navy: "bg-secondary text-primary",
};

export default function Builder() {
  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="builder-page">
      <PageHeader
        eyebrow="Execution module"
        title="Orixa Builder"
        subtitle="Transform validated opportunities into executable corporate structures. Each path produces boardroom-ready deliverables — not just analysis."
      />

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border" data-testid="builder-grid">
        {BUILDER_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <Link
              key={card.id}
              to={`/workflows/${card.id}`}
              className="group bg-background hover:bg-card transition-colors p-6 sm:p-8 flex flex-col"
              data-testid={`builder-card-${card.id}`}
            >
              <div className="flex items-start justify-between mb-5">
                <div className={`h-12 w-12 rounded-lg grid place-items-center ${ACCENT_MAP[card.accent] || ACCENT_MAP.navy}`}>
                  <Icon size={22} weight="duotone" />
                </div>
                <div className="text-overline text-muted-foreground">Builder</div>
              </div>
              <div className="font-display text-xl sm:text-2xl tracking-tight leading-tight mb-2">{card.title}</div>
              <div className="text-sm text-muted-foreground mb-4 leading-relaxed">{card.tagline}</div>
              <div className="mt-auto pt-4 border-t border-border">
                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">Outputs</div>
                <div className="text-xs text-muted-foreground leading-snug">{card.output}</div>
              </div>
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                Launch builder <ArrowRight size={14} weight="bold" />
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-12 bg-primary text-primary-foreground rounded-2xl p-6 sm:p-8 card-elev" data-testid="builder-cbc-cta">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 bg-white/10 rounded-lg grid place-items-center flex-shrink-0">
            <Lightning size={22} weight="duotone" className="text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 mb-1">Optional partner network</div>
            <div className="font-display text-xl sm:text-2xl tracking-tight leading-tight">Working with an external partner?</div>
            <div className="text-sm opacity-90 mt-2 leading-relaxed max-w-2xl">
              Orixa Invest is a software subscription. Independently of the platform, an unaffiliated partner (Consultis Business Consulting) may accept selected mandates for execution work — structuring, regulatory liaison, investor introductions and post-deal monitoring. Contracting occurs directly with them and is separate from your Orixa Invest subscription.
            </div>
          </div>
          <a href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest" target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-2 bg-white text-primary rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-white/95">
            Book a product demo <ArrowRight size={14} weight="bold" />
          </a>
        </div>
      </div>
    </div>
  );
}
